/**
 * English copy.
 *
 * Tone rules this file is reviewed against before every release (spec §7, §15):
 *
 * - The voice is the user's own past intention, never the app's judgement.
 *   "You said evenings matter" — not "you should read more".
 * - No guilt, no streaks, no loss aversion, no celebration of one choice over
 *   another. The three Comeback outcomes carry equal dignity in *words* as well
 *   as in pixels: all three are written in the user's first person, none is
 *   praised, none is softened, and none carries a hint or sub-label the others
 *   do not.
 * - Insights are observational, never causal, and come from a closed template
 *   set — never assembled from fragments at runtime.
 * - The words "come back", lowercase, are the recurring motif.
 * - The word "values" is never shown to a user. Internally these are
 *   *directions*; on screen they are "what matters to you".
 * - Strings marked *spoken* are read aloud by the user. They are reviewed for
 *   speakability, not just readability — read them out before changing them.
 */

export const en = {
  'app.name': 'Comeback',
  'app.tagline': 'Attention training',
  'app.motif': 'come back',

  // ---------------------------------------------------------------- the Catch
  // Tap 1. Nothing above it, nothing to read first — this button is the whole
  // screen, and it is tappable before any stored state has loaded.
  'catch.button': 'Caught it',
  'catch.today': 'Today',
  // Shown while onboarding is unfinished. An offer, not a prompt: it sits
  // beside "Today" as one more quiet option, never as a banner, a badge, or a
  // thing that has to be dismissed.
  'catch.setUp': 'Set up',
  // Shown briefly after a Catch is logged, then it fades. No count, no praise,
  // no streak — the app noting the fact and getting out of the way.
  'catch.logged': 'Logged.',

  // ------------------------------------------------------------------ the tag
  // Tap 2, which is where the Catch is written to the log. A user who stops
  // here has completed a full rep of skill one.
  'tag.question': "What's pulling?",
  'tag.bored': 'Bored',
  'tag.anxious': 'Anxious',
  'tag.tired': 'Tired',
  'tag.avoiding': 'Avoiding something',
  'tag.habit': 'Just habit',
  'tag.back': 'Back',

  // ------------------------------------------------------------- the Comeback
  // Tap 3. All three are the user's own words about their own choice. None is
  // recommended, none is discouraged, and none gets a hint the others lack —
  // "90 seconds" belongs on the surf screen, not under one of three buttons
  // where it would make that button the explained one.
  'resolve.redirect': 'Do what I planned',
  // Shown when there is no plan to point at yet — someone who left onboarding
  // for later, or archived everything. Three outcomes still, all equal; the app
  // just has nothing of theirs to quote back.
  'resolve.redirectNoPlan': 'Do something else instead',
  // The value anchor: the redirect carries its reason, in the user's own words
  // (spec §6.1). Declarative, and it credits the choice to them.
  'resolve.anchor': '{direction} — you chose that.',
  'resolve.surf': 'Ride it out',
  'resolve.deliberate': "Yes, I'm choosing this",

  // *Spoken.* Zero taps, whisper-friendly. An invitation, never an instruction
  // and never checked.
  'resolve.sayItCue': 'Say it, then do it.',

  // Leaving without a Comeback is not a failed interaction — it is a Catch,
  // which is a rep of the first skill. The wording says so without saying so.
  'resolve.leave': 'Leave it here',

  // Tap 4, optional, deliberate continuation only. A stated intention: nothing
  // fires when it elapses, there is no alarm, and "no limit" carries exactly
  // the same weight as the others. This is not a blocker in disguise.
  'until.question': 'Until when?',
  'until.10m': '10 minutes',
  'until.20m': '20 minutes',
  'until.untilEnds': 'Until this ends',
  'until.noLimit': 'No limit',
  'until.skip': 'Skip',

  // -------------------------------------------------------------- placeholder
  // I1 only. I2 replaces this with the user's own plan from onboarding, and its
  // own direction as the anchor.
  'plan.placeholder.action': 'Step outside for a minute',
  'plan.placeholder.anchor': 'Rest counts as much as anything else.',

  // ---------------------------------------------------------------- the today
  // A plain record, not a scoreboard. No totals framed as targets, no
  // comparison with yesterday, nothing that could be read as a quota.
  'today.title': 'Today',
  'today.empty': 'Nothing logged today.',
  'today.unresolved': 'Caught',
  'today.back': 'Back',


  // ------------------------------------------------------------- onboarding
  // The only long flow in the product (spec §6.1), ≤10 minutes, once. Its job
  // is to produce if-then plans the user actually recognises as their own, and
  // to set two expectations that decide how the next twelve weeks are read:
  // that a rising Catch count can be progress, and that the goal is to stop
  // needing this.
  //
  // The word "values" appears nowhere. These are directions, and on screen they
  // are simply "what matters to you".

  'onboard.intro.title': 'Comeback',
  'onboard.intro.body':
    "This part takes about ten minutes, once. After that it's one button you press when you notice you're running on autopilot.",
  'onboard.intro.note':
    "Nothing you write here leaves your phone. There's no account, and nothing is sent anywhere.",
  'onboard.intro.start': 'Start',
  'onboard.leave': 'Finish this later',

  // Layer 1 — directions. "Right now" is deliberate: this is not permanent
  // identity selection, and saying so is what keeps it from feeling like a test.
  'onboard.directions.title': 'What matters to you right now?',
  'onboard.directions.help': 'Pick up to three. Not forever — just now. You can change them whenever you like.',
  'onboard.direction.people': 'The people I love',
  'onboard.direction.body': 'My body',
  'onboard.direction.learning': 'Learning things',
  'onboard.direction.rest': 'Rest',
  'onboard.direction.presence': 'Being here',
  'onboard.direction.making': 'Making things',
  'onboard.direction.craft': 'Getting good at something',
  'onboard.direction.outside': 'Being outside',
  'onboard.direction.custom': 'Something else',
  'onboard.direction.customPlaceholder': 'In your own words',
  'onboard.direction.add': 'Add this direction',

  // Layer 2 — expressions. A direction becomes something you could actually do
  // this week. "Small is better than impressive" is doing real work here: the
  // most common failure of implementation intentions is an over-ambitious plan.
  'onboard.expressions.title': 'What does that look like in real life?',
  'onboard.expressions.help': 'One thing you would actually do. Small beats impressive.',
  'onboard.expressions.placeholder': 'Call my mother · Read ten pages · Lie down for ten minutes',

  // Layer 3 — target behaviours. Behaviour-neutral by rule (architecture A9):
  // nothing here assumes a phone.
  'onboard.behaviors.title': 'What pulls you away?',
  'onboard.behaviors.help':
    "One to three. This is not a confession — it is just where you would like to start noticing.",
  'onboard.behavior.scrolling': 'Scrolling',
  'onboard.behavior.checking': 'Checking for something new',
  'onboard.behavior.putting_off': 'Putting off one particular thing',
  'onboard.behavior.opening': 'Opening an app without deciding to',
  'onboard.behavior.custom': 'Something else',
  'onboard.behavior.customPlaceholder': 'In your own words',
  'onboard.behavior.add': 'Add this one',

  // The plans themselves — the output of the whole flow.
  'onboard.plans.title': 'When it happens, what will you do?',
  'onboard.plans.help':
    'Deciding now works better than deciding in the moment. That is most of what this app is.',
  'onboard.plans.when': 'When',
  'onboard.plans.then': "I'll",
  'onboard.plans.triggerPlaceholder': 'I catch myself scrolling',
  'onboard.plans.actionPlaceholder': 'Read ten pages',
  'onboard.plans.add': 'Add another',
  'onboard.plans.remove': 'Remove',
  'onboard.plans.count': '{done} of {min} written',

  'context.earlyMorning': 'Early morning',
  'context.morning': 'Morning',
  'context.afternoon': 'Afternoon',
  'context.evening': 'Evening',
  'context.night': 'Late',
  'context.any': 'Any time',

  // *Spoken.* Tier 2 (spec §4): vocalization is a technique we are testing, and
  // the copy says exactly that rather than claiming it works. No microphone is
  // ever requested and nothing is ever verified — said plainly, because a
  // prompt to speak aloud otherwise reads as a prompt to be recorded.
  'onboard.aloud.title': 'Say it once, out loud.',
  'onboard.aloud.help':
    'Quietly is fine. Nothing is listening — Comeback never asks for your microphone. Some people find saying it makes it stick; that is one of the things we are still testing.',
  'onboard.aloud.done': 'Said it',
  'onboard.aloud.skip': 'Skip',

  // The placement ritual — the keystone step, and the product's riskiest
  // assumption (spec §11, H1). The copy states our belief and marks it as a
  // belief, which is what claims discipline requires.
  'onboard.placement.title': 'Put Comeback where the pull is',
  'onboard.placement.body':
    "Install Comeback, then move it into the exact spot your problem app sits in now. When your thumb goes there out of habit, it lands here instead.",
  'onboard.placement.belief':
    'We think this is the step that matters most. It is also the one we are least sure about, and this beta is how we find out.',
  'onboard.placement.install': 'Install Comeback',
  'onboard.placement.installed': "It's installed",
  'onboard.placement.done': "Done — it's in place",
  'onboard.placement.later': 'Not now',
  'onboard.placement.reoffer':
    'You skipped the home-screen step. Want to do it now? We will not ask again.',
  'onboard.placement.reofferDo': 'Show me',
  'onboard.placement.reofferNo': 'No thanks',

  'install.ios.title': 'On iPhone, in Safari',
  'install.ios.step1': 'Tap the Share button at the bottom of the screen.',
  'install.ios.step2': 'Scroll down and tap "Add to Home Screen".',
  'install.ios.step3': 'Tap "Add".',
  'install.ios.step4': 'Press and hold the new icon, then drag it onto the spot your problem app sits in.',
  'install.android.title': 'On Android, in Chrome',
  'install.android.step1': 'Tap Install when Chrome offers it, or open the ⋮ menu and tap "Add to Home screen".',
  'install.android.step2': 'Confirm.',
  'install.android.step3': 'Press and hold the new icon, then drag it onto the spot your problem app sits in.',
  'install.desktop.title': 'On a computer',
  'install.desktop.step1': 'Use the install icon in the address bar, or your browser menu.',
  'install.desktop.step2': 'The home-screen step is for your phone — that is where the habit lives.',

  // The graduation arc, previewed on day one (spec §3.7: graduation is visible
  // from the start). This is also where the early-rise expectation is set, so
  // that a rising Catch count is not read as getting worse (spec §14).
  'onboard.arc.title': 'Where this goes',
  'onboard.arc.phase1': 'Weeks 1–4 — Comeback does the reminding.',
  'onboard.arc.phase2': 'Weeks 5–8 — it steps back and you start predicting.',
  'onboard.arc.phase3': 'Weeks 9–12 — it mostly stays quiet.',
  'onboard.arc.point': 'The goal is that you stop needing it.',
  'onboard.arc.earlyRise':
    'Your catch count may go up in the first weeks. That usually means you are noticing more, not slipping.',
  'onboard.arc.finish': "I'm ready",

  'onboard.done.title': "That's it.",
  'onboard.done.body': 'Press the button when you notice. Everything else is optional.',
  'onboard.done.go': 'come back',

  // ---------------------------------------------------------------- failures
  // Storage can fail — private browsing, no quota, an evicted database. The
  // wording blames the situation, never the person, and offers the only thing
  // that actually helps: try again.
  'error.notSaved': "That didn't save. Try again?",

  // --------------------------------------------------------------- the shell
  'update.ready': 'A new version is ready.',
  'update.action': 'Use it now',
  'update.dismiss': 'Later',
  'offline.ready': 'Comeback works offline from now on.',
} as const satisfies Record<string, string>;
